import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  PERMISSIONS_UPDATED_EVENT,
  getPermissionGroupMeta,
  getPermissions,
  hasPermission as hasPermissionKey,
} from "./permissionStorage";
import { refreshCurrentUserPermissions } from "./userPermissions";
import { PERMISSIONS_REFRESH_REQUEST_EVENT } from "./permissionStorage";

const PERMISSIONS_POLL_MS = 90_000;

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState(() => getPermissions());
  const [meta, setMeta] = useState(() => getPermissionGroupMeta());
  const [loading, setLoading] = useState(false);

  const syncFromStorage = useCallback(() => {
    setPermissions(getPermissions());
    setMeta(getPermissionGroupMeta());
  }, []);

  useEffect(() => {
    const onUpdated = () => syncFromStorage();
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, onUpdated);
    return () =>
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, onUpdated);
  }, [syncFromStorage]);

  const runRefresh = useCallback(async ({ background = false } = {}) => {
    if (!localStorage.getItem("token")) return;
    if (!background) setLoading(true);
    try {
      await refreshCurrentUserPermissions();
      syncFromStorage();
    } finally {
      if (!background) setLoading(false);
    }
  }, [syncFromStorage]);

  useEffect(() => {
    runRefresh({ background: false }).catch(() => {});
  }, [runRefresh]);

  useEffect(() => {
    if (!localStorage.getItem("token")) return;

    const onFocus = () => {
      runRefresh({ background: true }).catch(() => {});
    };
    const onRefreshRequest = () => {
      runRefresh({ background: true }).catch(() => {});
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        onFocus();
      }
    });
    window.addEventListener(PERMISSIONS_REFRESH_REQUEST_EVENT, onRefreshRequest);

    const intervalId = window.setInterval(() => {
      runRefresh({ background: true }).catch(() => {});
    }, PERMISSIONS_POLL_MS);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(
        PERMISSIONS_REFRESH_REQUEST_EVENT,
        onRefreshRequest,
      );
      window.clearInterval(intervalId);
    };
  }, [runRefresh]);

  const refresh = useCallback(async () => {
    await runRefresh();
  }, [runRefresh]);

  const hasPermission = useCallback(
    (key) => hasPermissionKey(key),
    [permissions],
  );

  const value = useMemo(
    () => ({
      permissions,
      meta,
      loading,
      refresh,
      hasPermission,
    }),
    [permissions, meta, loading, refresh, hasPermission],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    return {
      permissions: getPermissions(),
      meta: getPermissionGroupMeta(),
      loading: false,
      refresh: refreshCurrentUserPermissions,
      hasPermission: hasPermissionKey,
    };
  }
  return ctx;
}
